import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { rootUrl } from "../../../config/config";

const STATUS_STYLES = {
    IN_STOCK: "bg-green-100 text-green-800",
    ISSUED: "bg-blue-100 text-blue-800",
    SOLD: "bg-purple-100 text-purple-800",
    REMOVED: "bg-gray-200 text-gray-700",
};

const MOVEMENT_LABELS = {
    OPENING: "Opening stock",
    RECEIVED: "Received (purchase)",
    ADJUST_IN: "Adjustment in",
    ADJUST_OUT: "Adjustment out",
    TRANSFER: "Transfer",
    ISSUED: "Issued to job card",
    FITTED_EXTERNAL: "Fitted (external job card)",
    RETURNED: "Returned from job card",
    SOLD: "Sold",
    REVERSED: "Reversed",
};

const formatDateTime = (value) =>
    value
        ? new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : "-";

const statusLabel = (status) => (status ? status.replace("_", " ") : "-");

export default function SerialLookup() {
    const [searchParams] = useSearchParams();
    const [serialNo, setSerialNo] = useState((searchParams.get("serial_no") || "").toUpperCase());
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Opened from a link such as /parts/serials?serial_no=ABC123: search straight away
    useEffect(() => {
        const fromLink = (searchParams.get("serial_no") || "").trim().toUpperCase();
        if (fromLink) {
            setSerialNo(fromLink);
            runSearch(fromLink);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleSearch = (e) => {
        e.preventDefault();
        runSearch(serialNo);
    };

    const runSearch = async (value) => {
        if (!value.trim()) {
            setError("Enter a serial number to search");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.get(`${rootUrl}/api/parts/serials/lookup`, {
                params: { serial_no: value.trim() },
                headers: { Authorization: `Bearer ${token}` },
            });
            setResults(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to look up serial number");
            setResults(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-4 md:p-6">
            <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-4 md:p-6 space-y-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Serial Number Lookup</h1>
                    <p className="text-gray-600 mt-1">Find where a serial-tracked unit is and everything that happened to it</p>
                </div>

                <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                        <input
                            value={serialNo}
                            onChange={(e) => setSerialNo(e.target.value.toUpperCase())}
                            placeholder="e.g. BAT12345"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono uppercase"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-[#FF5733] text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400"
                    >
                        {loading ? "Searching..." : "Search"}
                    </button>
                </form>
                {error && <p className="text-sm text-red-500">{error}</p>}

                {results && results.length === 0 && (
                    <p className="text-gray-600">No unit found with serial number {serialNo.trim()}.</p>
                )}

                {results && results.map((serial) => (
                    <div key={serial.id} className="border border-gray-200 rounded-xl p-4 space-y-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <p className="font-mono text-lg font-bold text-gray-900">{serial.serial_no}</p>
                                <p className="text-sm text-gray-600">
                                    {serial.part_name} ({serial.part_number}) · {serial.part_id}
                                </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[serial.status] || ""}`}>
                                {statusLabel(serial.status)}
                                {serial.source === "EXTERNAL" ? " · EXTERNAL" : ""}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div>
                                <p className="text-gray-500">Location</p>
                                <p className="font-medium">{serial.location || "-"}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Vehicle</p>
                                <p className="font-medium">{serial.plate_no || "-"}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Job Card</p>
                                <p className="font-medium">
                                    {serial.job_card_id ? (
                                        <Link to={`/job-cards/${serial.job_card_id}`} className="text-[#FF5733] hover:underline">
                                            {serial.job_card_id}
                                        </Link>
                                    ) : "-"}
                                </p>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[640px]">
                                <thead>
                                    <tr className="text-left text-xs uppercase text-gray-500 border-b">
                                        <th className="py-2 pr-3">Date</th>
                                        <th className="py-2 pr-3">Movement</th>
                                        <th className="py-2 pr-3">Status</th>
                                        <th className="py-2 pr-3">Location</th>
                                        <th className="py-2 pr-3">Job Card / Vehicle</th>
                                        <th className="py-2 pr-3">Reference</th>
                                        <th className="py-2">By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {serial.movements.map((m) => (
                                        <tr key={m.id} className="border-b border-gray-100 align-top">
                                            <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(m.input_date)}</td>
                                            <td className="py-2 pr-3">
                                                {MOVEMENT_LABELS[m.movement_type] || m.movement_type}
                                                {m.notes && <div className="text-xs text-gray-500">{m.notes}</div>}
                                            </td>
                                            <td className="py-2 pr-3 whitespace-nowrap">{statusLabel(m.from_status)} → {statusLabel(m.to_status)}</td>
                                            <td className="py-2 pr-3">
                                                {m.from_location !== m.to_location ? `${m.from_location || "-"} → ${m.to_location || "-"}` : (m.to_location || "-")}
                                            </td>
                                            <td className="py-2 pr-3">{[m.job_card_id, m.plate_no].filter(Boolean).join(" · ") || "-"}</td>
                                            <td className="py-2 pr-3 font-mono text-xs">{m.parts_trans_id || "-"}</td>
                                            <td className="py-2">{m.input_by}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
